import { PROJECT_SUMMARY_WITH_REPORT } from 'constants/routePaths';

import * as React from 'react';
import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import ProjectByIdQuery from '@graphql/queries/ProjectByIdQuery';
import ReportProductsByReportIdQuery, {
  ReportProductsByReportIdResponse,
} from '@graphql/queries/ReportProductsByReportIdQuery';
import { KeyboardBackspace as KeyboardBackspaceIcon } from '@mui/icons-material';
import { IconButton, Paper } from '@mui/material';
import { Grid } from '@mui/material';
import ReportOfpDescription from 'components/Report/ReportOfpDescription';
import { QaPreviewReportProvider } from 'hooks/QaPreviewReportContext';
import useQaPreviewSession from 'hooks/useQaPreviewSession';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouterProps } from 'react-router';
import { useLocation } from 'react-router-dom';
import formatPath from 'utils/formatPath';
import { getQaPreviewSession } from 'utils/qaPreviewSession';

import styles from './ReportProductDetails.module.css';
import LoadingScreen from '../../components/LoadingScreen';
import FlavorDecompositionTable from '../../components/Report/FlavorDecomposition/FlavorDecompositionTable';
import ReportProductDetailsComponent from '../../components/Report/ReportProductDetails';
import selectWorkspaceProducerId from '../../selectors/workspaceProducerId';
import { ProjectTabs } from '../../services/customerPreferences';
import { PageHeader } from '../../styles/themeComponents';
import { checkIsWrongWorkspace } from '../../utils/afsUtils';

interface ReportProps extends RouterProps {
  match: Record<string, any>;
  reportId: string;
  productId: number;
  version?: string;
}

// Clean the projectId param, as it might not be a number
export const cleanProjectId = (projectIdParam: string): number => {
  const parsedProjectId = parseInt(projectIdParam);

  if (!isNaN(parsedProjectId)) {
    return parsedProjectId;
  } else {
    return undefined;
  }
};

// Clean the version param, as it might be the null string
export const cleanVersionParam = (versionParam: string): string => {
  if (!!versionParam && versionParam != 'null') {
    return decodeURI(versionParam);
  } else {
    return null;
  }
};

const ReportProductDetails: React.FC<ReportProps> = (props) => {
  const { match, history } = props;
  const location = useLocation();
  const { state }: any = location;

  const { t } = useTranslation();

  const projectId = cleanProjectId(match.params.projectId);
  const reportId = match.params.reportId;
  const productId = parseInt(match.params.productId);
  const version = cleanVersionParam(match.params.version);

  const workspaceId = useSelector((state) => selectWorkspaceProducerId(state));

  // QA reviewers previewing an unapproved report bypass the project/workspace check
  const qaPreview = useQaPreviewSession(reportId, projectId);

  const { data: projectCheck, loading: projectCheckLoading } =
    useQuery<reports.ProjectByIdResponse>(ProjectByIdQuery, {
      variables: { projectId },
      skip: projectId === undefined || !!qaPreview,
    });

  const { data: reportProductsData, loading: reportProductsLoading } =
    useQuery<ReportProductsByReportIdResponse>(ReportProductsByReportIdQuery, {
      variables: { reportId },
      skip:
        !reportId ||
        (!qaPreview &&
          (projectCheckLoading ||
            checkIsWrongWorkspace(
              projectId,
              projectCheck,
              workspaceId,
              reportId,
            ))),
    });

  const isWrongWorkspace =
    reportProductsLoading ||
    (!qaPreview &&
      checkIsWrongWorkspace(
        projectId,
        projectCheck,
        workspaceId,
        reportId,
        productId,
        reportProductsData,
      ));

  useEffect(() => {
    return () => {
      history.listen(() => {
        window.scrollTo(0, 0);
      });

      if (
        history.action === 'POP' &&
        reportId &&
        projectId !== undefined &&
        !getQaPreviewSession(reportId, projectId)
      ) {
        if (
          state &&
          state.routeBackLocation &&
          state.routeBackLocation == ProjectTabs.EXECUTIVE_SUMMARY
        )
          return history.replace({
            pathname: formatPath(PROJECT_SUMMARY_WITH_REPORT, {
              projectId,
              reportId,
            }),
            state: {
              productId,
              version,
              tabIdx: ProjectTabs.EXECUTIVE_SUMMARY,
            },
          });

        return history.replace({
          pathname: formatPath(PROJECT_SUMMARY_WITH_REPORT, {
            projectId,
            reportId,
          }),
          state: {
            productId,
            version,
            tabIdx: ProjectTabs.PROJECT_SUMMARY,
          },
        });
      }
    };
  }, []);

  if (projectCheckLoading || reportProductsLoading) {
    return <LoadingScreen />;
  }

  if (isWrongWorkspace) {
    return (
      <div>
        <h2>{t('errors.accessForbidden', 'Access Forbidden')}</h2>
        <p>
          {t(
            'errors.noProjectAccess',
            'You do not have access to view this project. Please ensure you are in the correct workspace.',
          )}
        </p>
      </div>
    );
  }

  return (
    <QaPreviewReportProvider session={qaPreview}>
      <Paper className={styles.container}>
        <Grid key={productId} container justifyContent="center">
          <Grid item xs={12}>
            <div className={styles.header}>
              <IconButton
                onClick={history.goBack}
                size="small"
                style={{ marginLeft: -26 }}
              >
                <KeyboardBackspaceIcon fontSize="small" />
                <PageHeader>
                  {t('reports.reportProductDetails.back')}
                </PageHeader>
              </IconButton>
            </div>
          </Grid>
          <Grid style={{ marginTop: '30px' }} item xs={12}>
            <ReportProductDetailsComponent
              projectId={projectId}
              reportId={reportId}
              productId={productId}
              productVersion={version}
            />
          </Grid>
          <Grid style={{ marginTop: '30px' }} item xs={12}>
            <ReportOfpDescription
              reportId={reportId}
              productId={productId}
              productVersion={version}
            />
          </Grid>
          <Grid style={{ marginTop: '30px' }} item xs={12}>
            <FlavorDecompositionTable
              projectId={projectId}
              reportId={reportId}
              productId={productId}
              version={version}
              forceNoTuning={false}
            />
          </Grid>
        </Grid>
      </Paper>
    </QaPreviewReportProvider>
  );
};

export default ReportProductDetails;
